import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI } from '../lib/api';
import ProjectAIHelper from './ProjectAIHelper';
import { 
  LayoutDashboard, 
  FolderKanban, 
  Plane, 
  Users, 
  Link2, 
  MessageSquare, 
  Clock, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Map,
  Film,
  Bot
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_b5cb1511-d064-4852-992c-1923642a6a2e/artifacts/a9e22iqs_k.jbkln..mp4";

const Layout = () => {
  const { user, logout, isSuperAdmin, isAdmin, isSupervisor, isPilot, isLeader, isVigilant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectHasMap, setProjectHasMap] = useState(false);
  const [projectHasVideos, setProjectHasVideos] = useState(false);

  // Fetch project has_map and has_videos for project-scoped roles
  useEffect(() => {
    const checkProjectFeatures = async () => {
      if (!isSuperAdmin && !isPilot && user?.project_id) {
        try {
          const res = await projectsAPI.get(user.project_id);
          setProjectHasMap(res.data.has_map || false);
          setProjectHasVideos(res.data.has_videos || false);
        } catch (e) { setProjectHasMap(false); setProjectHasVideos(false); }
      }
      if (isPilot && user?.project_id) {
        try {
          const res = await projectsAPI.get(user.project_id);
          setProjectHasVideos(res.data.has_videos || false);
        } catch (e) { setProjectHasVideos(false); }
      }
    };
    checkProjectFeatures();
  }, [isSuperAdmin, isPilot, user?.project_id]);

  const navItems = [
    { path: '/dashboard', label: 'DASHBOARD', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'supervisor', 'leader', 'vigilant'] },
    { path: '/projects', label: 'PROJETOS', icon: FolderKanban, roles: ['super_admin'] },
    { path: '/my-project', label: 'MEU PROJETO', icon: FolderKanban, roles: ['admin', 'supervisor', 'pilot', 'leader', 'vigilant'] },
    { path: '/drones', label: 'DRONES', icon: Plane, roles: ['super_admin', 'admin', 'supervisor'] },
    { path: '/users', label: 'USUARIOS', icon: Users, roles: ['super_admin'] },
    { path: '/links', label: 'LINKS', icon: Link2, roles: ['super_admin'] },
    { path: '/chat', label: 'CHAT', icon: MessageSquare, roles: ['super_admin', 'admin', 'supervisor', 'pilot', 'leader', 'vigilant'] },
    { path: '/time', label: 'JORNADA', icon: Clock, roles: ['super_admin', 'admin', 'supervisor', 'pilot'] },
    { path: '/videos', label: 'MIDIAS', icon: Film, roles: ['super_admin', 'admin', 'supervisor', 'pilot'] },
    { path: '/map', label: 'MAPA', icon: Map, roles: ['super_admin', 'admin', 'supervisor', 'leader', 'vigilant'] },
    { path: '/ai', label: 'ASSISTENTE IA', icon: Bot, roles: ['super_admin'] },
    { path: '/activity', label: 'ATIVIDADE', icon: Users, roles: ['super_admin'] },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    // For project-scoped roles, show MAPA only if their project has has_map enabled
    if (item.path === '/map' && !isSuperAdmin) return projectHasMap;
    // For project-scoped roles, show VIDEOS only if their project has has_videos enabled
    if (item.path === '/videos' && !isSuperAdmin) return projectHasVideos;
    return true;
  });

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = () => {
    if (isSuperAdmin) return { label: 'SUPER ADMIN', className: 'badge-warning' };
    if (isAdmin) return { label: 'ADMIN', className: 'badge-info' };
    if (user?.role === 'supervisor') return { label: 'SUPERVISOR', className: 'badge-info' };
    if (isPilot) return { label: 'PILOTO', className: 'badge-active' };
    if (isLeader) return { label: 'LIDER', className: 'badge-active' };
    if (isVigilant) return { label: 'VIGILANTE', className: 'badge-active' };
    return { label: 'USER', className: 'badge-status' };
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gray-50 flex">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden text-navy hover:bg-gray-100 bg-white shadow-lg rounded-xl"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
        aria-label="Menu de navegacao"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </Button>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 z-50 shadow-lg
                   transform transition-transform duration-300 lg:translate-x-0 flex flex-col
                   ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar"
      >
        {/* Video Header - Full Width */}
        <div className="border-b border-gray-200 bg-[#1f2a36] flex items-center justify-center">
          <video 
            src={VIDEO_URL}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-auto object-cover"
            data-testid="logo"
          />
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center shadow-sm">
              <span className="text-navy font-rajdhani font-bold text-lg">
                {user?.name?.[0] || user?.username?.[0] || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-navy font-medium truncate text-sm">
                {user?.name || user?.username}
              </p>
              <span className={roleBadge.className}>
                {roleBadge.label}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 min-h-0">
          <nav className="py-4">
            {filteredNavItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`nav-item w-full animate-slide-right ${isActive ? 'active' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <Icon size={18} />
                  <span className="font-rajdhani font-medium tracking-wider text-sm">
                    {item.label}
                  </span>
                  {isActive && <ChevronRight size={16} className="ml-auto" />}
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="nav-item w-full text-red-500 hover:text-red-600 hover:bg-red-50 hover:border-red-500"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span className="font-rajdhani font-medium tracking-wider text-sm">SAIR</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen min-h-[100dvh]">
        {/* Top Bar */}
        <header className="sticky top-0 w-full h-14 sm:h-16 bg-white/95 border-b border-gray-200 backdrop-blur-md z-30 flex items-center justify-between px-4 sm:px-6 lg:px-8 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="flex items-center gap-4 ml-12 lg:ml-0">
            <h1 className="text-base sm:text-lg font-rajdhani font-semibold text-navy uppercase tracking-wider truncate max-w-[50vw] sm:max-w-none">
              {filteredNavItems.find(item => item.path === location.pathname)?.label || 'RPAS'}
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span className="text-red-500 text-[10px] sm:text-xs font-medium">ATIVO</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 2xl:p-10 animate-fade-in pb-safe">
          <Outlet />
        </div>
      </main>
      <ProjectAIHelper />
    </div>
  );
};

export default Layout;
